import { useEffect, useMemo } from "react"

import { MoreOutlined, StarOutlined } from "@ant-design/icons"
import { Col, Divider, Dropdown, Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import Button from "../../../../../Components/Button"
import { PaymentDefaultTag } from "../../../../../Components/Payments/PaymentDefaultTag"
import ShopifyDefaultRow from "../../../../../Components/Payments/ShopifyDefaultRow"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { CARD_BRANDS } from "../../../../../Utils/cardBrands"
import { getJourneyState, JOURNEY_FLOWS } from "../../../../../Utils/onboarding/getJourneyState"
import AppUtils, { createAccountStepPath, onboardingStepPath } from "../../../../../Utils/utils"
import { usePageQuery } from "../../../../BusinessSettings/usePageQuery"
import { BA_STEPS } from "../../../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../../../GettingStartedPage/utils/onboardingSteps"
import UserCreators from "../../../../User/reducer"

const { Text } = Typography

const PaymentMethods = ({
  businessAccountId,
  usersCards,
  paymentMethodsRef,
  isAdmin,
  userInfo,
  userState,
  balance,
  bottomPadding,
  isShopifyOnlyUser,
  isShopifyDefaultProcessor,
  ...props
}) => {
  const { isMobile, theme } = useSelector((state) => state.nav)
  const { pathname } = useLocation()
  const { search } = useLocation()
  const { user_id } = usePageQuery(search)
  const navigate = useNavigate()
  const journey = getJourneyState({
    userInfo,
    userState,
    pathname
  })

  const { t } = useTranslation()

  const items = useMemo(
    () => [
      {
        key: 0,
        title: "Make default",
        label: (
          <Flex>
            <StarOutlined />
            {isShopifyDefaultProcessor ? "Make Backup" : t("Make default")}
          </Flex>
        )
      },
      {
        key: 1,
        title: "Remove",
        label: (
          <Flex>
            <Icon type={"delete"} />
            {t("Remove")}
          </Flex>
        )
      }
    ],
    [isShopifyDefaultProcessor]
  )

  // useEffect(() => {
  //   if (businessAccountId || user_id) {
  //     props.getUsersCards({
  //       //...(user_id ? { user_id } : { business_account_id: businessAccountId })
  //        user_id: user_id || userInfo?.id
  //     });
  //   }
  // }, [businessAccountId, user_id, userInfo?.id]);

  useEffect(() => {
    if (user_id || userInfo?.id) {
      props.getUsersCards({
        user_id: user_id || userInfo?.id
      })
    }
  }, [user_id, userInfo?.id])

  const actionClick = (key, id) => {
    if (isAdmin) return
    if (+key) {
      props.deleteCard({ card_id: id })
    } else {
      props.makeDefaultCard({ card_id: id, data: { active: true } })
    }
  }

  const handleAddPaymentMethod = () => {
    if (journey.flow === JOURNEY_FLOWS.INVITED_CREATE_BUSINESS) {
      navigate(createAccountStepPath[BA_STEPS.CARD])
      return
    }
    // if (!balance?.["activated"]) return;
    navigate(onboardingStepPath[OB_STEPS.CARD])
  }

  return (
    <Flex justify="space-between" style={{ width: "100%", paddingBottom: bottomPadding && 130 }}>
      <Col ref={paymentMethodsRef} flex="1 1 420" style={{ width: "100%" }}>
        <Text strong>{isShopifyOnlyUser ? "How you will pay" : t("Select payment method")}</Text>
        {usersCards && (
          <Col style={{ marginTop: 12, paddingLeft: 0, width: "100%" }}>
            {isShopifyDefaultProcessor && (
              <div
                style={{
                  paddingLeft: isMobile ? 0 : 12
                }}
              >
                <ShopifyDefaultRow iconSize={20} />
              </div>
            )}
            {[...usersCards].map((card) => (
              <div key={card.id}>
                <Flex
                  align="center"
                  style={{
                    marginTop: 8,
                    paddingLeft: isMobile ? 0 : 12,
                    width: "100%"
                  }}
                >
                  {/* card info — fixed left */}
                  <Flex align="center" gap={8} style={{ flex: "1 1 0", minWidth: 0 }}>
                    {card.brand === "visa" ? (
                      <Icon type={card.brand} />
                    ) : CARD_BRANDS.includes(card.brand) ? (
                      <img
                        src={Images[`${card.brand}${theme === "dark" ? "_dark" : ""}`]}
                        alt={card.brand}
                      />
                    ) : null}
                    <Text style={{ whiteSpace: "nowrap" }}>
                      {AppUtils.toUpperLatter(card.brand)}
                    </Text>
                    {card.is_default && (
                      <div>
                        <PaymentDefaultTag
                          style={{
                            ...(isShopifyDefaultProcessor
                              ? {
                                  color: theme === "dark" ? "#7A7A83" : "#7A7A83",
                                  background: theme === "dark" ? "#1D1D1D" : "#F4F4F5"
                                }
                              : {})
                          }}
                        >
                          {isShopifyDefaultProcessor ? "Backup" : t("Default")}
                        </PaymentDefaultTag>
                      </div>
                    )}
                  </Flex>

                  {/* last4 — fixed width column */}
                  <Text
                    type="secondary"
                    strong
                    style={{ width: 50, textAlign: "right", flexShrink: 0 }}
                  >
                    {card.last4}
                  </Text>

                  <Divider type="vertical" style={{ margin: "0 8px" }} />

                  {/* expires — fixed width column */}
                  <Text type="secondary" strong style={{ width: 120, flexShrink: 0 }}>
                    {t("Expires on")} {card.exp_month > 9 ? card.exp_month : `0${card.exp_month}`}/
                    {String(card.exp_year).slice(2, 4)}
                  </Text>

                  {/* action — fixed width column */}
                  <div
                    style={{
                      width: 32,
                      flexShrink: 0,
                      display: "flex",
                      justifyContent: "center"
                    }}
                  >
                    {!card.is_default && !isAdmin ? (
                      <Dropdown
                        overlayClassName="custom-menu"
                        menu={{
                          items,
                          onClick: ({ key }) => actionClick(key, card.id)
                        }}
                        trigger={["click"]}
                        placement="bottomLeft"
                        overlayStyle={{ minWidth: 200 }}
                      >
                        <Button
                          size="small"
                          icon={<MoreOutlined />}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            borderRadius: 6
                          }}
                        />
                      </Dropdown>
                    ) : null}
                  </div>
                </Flex>
                <Divider style={{ margin: "12px 0px" }} />
              </div>
            ))}
          </Col>
        )}
        <Flex gap={4} style={{ paddingLeft: 12 }} align="center" justify="space-between">
          <Button
            onClick={handleAddPaymentMethod}
            type="link"
            disabled={false}
            style={{
              padding: "0",
              fontWeight: 400,
              height: 22,
              color: "#225AEA"
            }}
          >
            <Icon type={"plus"} />
            {t("Add credit or debit card")}
          </Button>

          <Flex gap={4}>
            <Icon type={"visa"} />
            <Icon type={"mastercard"} />
            <Icon type={"Americanexpress"} />
            <Icon type={"discover"} />
          </Flex>
        </Flex>
      </Col>
    </Flex>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.user.businessAccount?.id,
  usersCards: state.user.usersCards,
  isAdmin: state.auth.isAdmin,
  userInfo: state.auth.userInfo,
  userState: state.user,
  balance: state.balance.balance
})

const mapDispatchToProps = (dispatch) => ({
  getUsersCards: (params) => dispatch(UserCreators.getUsersCardsRequest(params)),
  deleteCard: (card_id) => dispatch(UserCreators.deleteUserCardRequest(card_id)),
  makeDefaultCard: ({ card_id, data }) =>
    dispatch(UserCreators.updateDefaultCardRequest({ card_id, data }))
})

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMethods)
